import React from "react";
import Table from "antd/es/table";
import { ColumnType } from "antd/es/table";
import MyPagination from "../../../views/components/Pagination";
import {
  useContactDelete,
  useContactList,
} from "../../../hooks/api/contact/hooks";
import { IContactPayload } from "../../../types/api/contact";
import moment from "moment";
import { Button, message, Popconfirm } from "antd";
import { DeleteIcon, Trash2 } from "lucide-react";

const ContctList: React.FC<{ allowURLStateUpdate?: boolean }> = ({
  allowURLStateUpdate = true,
}) => {
  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useContactList({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });

  const { mutate: deleteContactById, isPending: isDeleting } =
    useContactDelete();

  const handleDelete = (id: string) => {
    deleteContactById(id, {
      onSuccess: () => {
        message.success("Contact deleted");
        refetch({
          page: 1,
          limit: 20,
          ...DEFAULT_NUQS_CONFIG,
        });
      },
      onError: () => {
        message.error("Failed to delete contact");
      },
    });
  };

  const contactList: IContactPayload[] = data?.data || [];
  const qData = data?.qData ?? { page: 1, limit: 20 };

  const columns: ColumnType<IContactPayload>[] = [
    {
      title: "SN",
      key: "sn",
      width: 70,
      render: (_: any, __: any, index: number) => (
        <span className="text-gray-800 font-medium">{index + 1}</span>
      ),
    },
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => (
        <a
          href={`mailto:${email}`}
          className="text-blue-600 underline"
          target="_blank"
        >
          {email}
        </a>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone: string) => (
        <a href={`tel:${phone}`} className="text-blue-600 underline">
          {phone}
        </a>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (msg: string) => (
        <span className="text-gray-800 line-clamp-2">{msg}</span>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => moment(date).format("DD MMM YYYY, hh:mm A"),
    },

    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_: any, record: IContactPayload) => (
        <Popconfirm
          title="Delete Contact"
          description="Are you sure you want to delete this contact?"
          onConfirm={() => handleDelete(record._id)}
          okText="Yes"
          cancelText="No"
        >
            <Trash2 className="text-red-500 cursor-pointer"/>
          
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div className="hidden sm:block">
        <Table
          dataSource={contactList}
          columns={columns}
          rowKey={(item: any) => item._id}
          pagination={false}
          size="middle"
          bordered={false}
          loading={isFetching}
          className="custom-table-class rounded-lg border border-gray-200"
          scroll={{ x: true }}
          footer={() => (
            <MyPagination
              qData={qData}
              onChange={refetch}
              total={qData.total}
              DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
            />
          )}
        />
      </div>

      {/* MOBILE CARD VIEW */}
      <div className="sm:hidden space-y-4 mt-4">
        {contactList.length > 0 ? (
          contactList.map((item, index) => (
            <div
              key={item._id}
              className="bg-[#F9F9F9] shadow-md rounded-lg p-4 border border-gray-200"
            >
              <p className="text-sm text-gray-500 font-medium">
                SN: {index + 1}
              </p>

              <p className="text-lg font-semibold text-gray-900 mt-1">
                {item.fullName}
              </p>

              <p className="mt-2">
                <span className="font-semibold">Email:</span>{" "}
                <a
                  href={`mailto:${item.email}`}
                  target="_blank"
                  className="text-blue-600 underline"
                >
                  {item.email}
                </a>
              </p>

              {/* Phone */}
              <p>
                <span className="font-semibold">Phone:</span>{" "}
                <a
                  href={`tel:${item.phone}`}
                  className="text-blue-600 underline"
                >
                  {item.phone}
                </a>
              </p>

              {/* Message */}
              <p className="mt-2">
                <span className="font-semibold">Message:</span>
                <span className="block text-gray-800">{item.message}</span>
              </p>

              {/* Date */}
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-semibold">Date:</span>{" "}
                {moment(item?.createdAt).format("DD MMM YYYY, hh:mm A")}
              </p>
            </div>
          ))
        ) : (
          <p className="text-center py-6 text-gray-500">No leads found</p>
        )}
      </div>
    </div>
  );
};

export default ContctList;
